import { ChangeEventHandler, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Input, Typography } from '@screentone/core';

import styles from './Autocomplete.module.scss';

interface AutocompleteProps<T> {
  value: string;
  onChange: (value: string) => void;
  options: T[];
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  maxOptions?: number;
  filterOptions?: (options: T[], inputValue: string) => T[];
  className?: string;
  required?: boolean;
  error?: boolean;
  getOptionKey?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  renderOption?: (option: T, onClick: () => void) => ReactNode;
  renderHeader?: () => ReactNode;
  onOpen?: () => void;
  'data-testid'?: string;
}

export const Autocomplete = <T,>({
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  isLoading = false,
  maxOptions = 10,
  filterOptions,
  className,
  required = false,
  error = false,
  getOptionKey = (option: T) => String(option),
  getOptionValue = (option: T) => String(option),
  renderOption,
  renderHeader,
  onOpen,
  'data-testid': dataTestId
}: AutocompleteProps<T>) => {
  // Default filter function that works with any type T
  const defaultFilterOptions = (opts: T[], inputValue: string): T[] => {
    const trimmedInput = inputValue.trim().toLowerCase();
    if (trimmedInput === '') return opts;
    return opts.filter((option) => getOptionValue(option).toLowerCase().includes(trimmedInput));
  };

  const actualFilterOptions = filterOptions ?? defaultFilterOptions;
  const [isAutocompleteShown, setIsAutocompleteShown] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange(e.target.value);
  };

  const handleShowAutocomplete = () => {
    setIsAutocompleteShown(true);
    onOpen?.();
  };

  const handleClickOption = (option: T) => {
    onChange(getOptionValue(option));
    setIsAutocompleteShown(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setIsAutocompleteShown(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredOptions = actualFilterOptions(options, value);
  const displayOptions = value.trim() === '' ? filteredOptions : filteredOptions.slice(0, maxOptions);
  const showDropdown = isAutocompleteShown && (isLoading || displayOptions.length > 0);

  return (
    <div ref={autocompleteRef} className={`${styles.autocomplete} ${className ?? ''}`}>
      <Input
        data-testid={dataTestId}
        placeholder={placeholder}
        onChange={handleChange}
        value={value}
        onClick={handleShowAutocomplete}
        disabled={disabled}
        required={required}
        error={error}
      />
      {showDropdown && (
        <Box data-testid="autocomplete-list" className={styles.autocompleteBox}>
          {renderHeader?.()}
          {isLoading ? (
            <Box padding={{ all: 'sm' }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : renderOption ? (
            displayOptions.map((option: T) => (
              <div key={getOptionKey(option)}>{renderOption(option, () => handleClickOption(option))}</div>
            ))
          ) : (
            displayOptions.map((option: T) => (
              <Box
                key={getOptionKey(option)}
                padding={{ all: 'sm' }}
                className={styles.option}
                onClick={() => handleClickOption(option)}
              >
                <Typography>{getOptionValue(option)}</Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </div>
  );
};
